import React, { useMemo, useState } from 'react';
import { Todo } from '@/hooks/useTodos';
import { Tooltip } from 'react-tooltip';
import { ChevronLeft, ChevronRight, Calendar, Grid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  todos: Todo[];
  onDateSelect?: (date: Date) => void;
  selectedDate?: Date | null;
}

type ViewMode = 'heatmap' | 'calendar';

const TodoHeatmap = ({ todos, onDateSelect, selectedDate }: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>('heatmap');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // --- Heatmap Logic ---
  const today = new Date();
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - today.getDay()); 
  const startDate = new Date(currentWeekStart);
  startDate.setDate(startDate.getDate() - (25 * 7));

  const activityMap = useMemo(() => {
    const map = new Map<string, number>();
    todos.forEach(todo => {
        if (todo.dueDate) {
             const dateStr = new Date(todo.dueDate).toISOString().split('T')[0];
             map.set(dateStr, (map.get(dateStr) || 0) + 1);
        }
    });
    return map;
  }, [todos]);

  const weeks = useMemo(() => {
    const weeksArr = [];
    const currentDate = new Date(startDate);
    const maxWeeks = 26;
    
    for (let w = 0; w < maxWeeks; w++) {
        const weekData = [];
        for (let d = 0; d < 7; d++) {
            const dateStr = currentDate.toISOString().split('T')[0];
            const count = activityMap.get(dateStr) || 0;
            
            weekData.push({
                date: dateStr,
                count,
                obj: new Date(currentDate)
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }
        weeksArr.push(weekData);
    }
    return weeksArr;
  }, [activityMap, startDate]);

  const getColor = (count: number, isSelected?: boolean) => {
      if (isSelected) return 'bg-orange-500 ring-2 ring-orange-200 dark:ring-orange-900';
      if (count === 0) return 'bg-slate-100 dark:bg-white/5';
      if (count <= 2) return 'bg-blue-100 dark:bg-blue-900/30'; 
      if (count <= 5) return 'bg-blue-300 dark:bg-blue-700/50';
      if (count <= 9) return 'bg-blue-500 dark:bg-blue-500';
      return 'bg-blue-700 dark:bg-blue-400';
  };

  const getBadgeColor = (count: number) => {
      if (count <= 2) return 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300';
      if (count <= 5) return 'bg-blue-500 text-white';
      return 'bg-blue-700 text-white';
  };

  // --- Calendar Logic ---
  const calendarDays = useMemo(() => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const days = [];
      // Fill blanks
      for (let i = 0; i < firstDay; i++) {
          days.push(null);
      }
      // Fill days
      for (let i = 1; i <= daysInMonth; i++) {
          const date = new Date(year, month, i);
          // Use local date string for comparison to avoid timezone issues
          // Construct YYYY-MM-DD manually to match activityMap keys
          const yearStr = date.getFullYear();
          const monthStr = String(date.getMonth() + 1).padStart(2, '0');
          const dayStr = String(date.getDate()).padStart(2, '0');
          const dateStr = `${yearStr}-${monthStr}-${dayStr}`;
          
          const count = activityMap.get(dateStr) || 0;
          days.push({ day: i, date, count });
      }
      return days;
  }, [currentMonth, activityMap]);

  return (
    <div className="w-full overflow-hidden">
        {/* Header with Toggle */}
        <div className="flex justify-between items-center mb-3 px-1">
            <div className="flex bg-slate-100 dark:bg-slate-700/50 p-1 rounded-lg">
                <button
                    onClick={() => setViewMode('heatmap')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'heatmap' ? 'bg-white dark:bg-slate-600 shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                    title="热力图模式"
                >
                    <Grid size={14} />
                </button>
                <button
                    onClick={() => setViewMode('calendar')}
                    className={`p-1.5 rounded-md transition-all ${viewMode === 'calendar' ? 'bg-white dark:bg-slate-600 shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                    title="日历模式"
                >
                    <Calendar size={14} />
                </button>
            </div>
            
            <AnimatePresence mode="wait">
                {viewMode === 'calendar' && (
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                    >
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <span>{currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月</span>
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>

        <motion.div layout className="relative overflow-hidden">
            <AnimatePresence initial={false}>
            {viewMode === 'heatmap' ? (
                <motion.div
                    key="heatmap"
                    initial={{ opacity: 0, position: 'absolute', width: '100%' }}
                    animate={{ opacity: 1, position: 'relative', width: '100%' }}
                    exit={{ opacity: 0, position: 'absolute', width: '100%' }}
                    transition={{ duration: 0.3 }}
                    className="w-full px-1 py-3 overflow-x-auto custom-scrollbar"
                >
                    <div className="flex gap-[2px] w-full min-w-[500px] sm:min-w-0">
                        {weeks.map((week, i) => (
                            <div key={i} className="flex flex-col gap-[2px] flex-1 min-w-0">
                                {week.map((day) => {
                                    const isSelected = selectedDate && day.date === selectedDate.toISOString().split('T')[0];
                                    return (
                                        <div
                                            key={day.date}
                                            className={`w-full h-3 rounded-[1px] ${getColor(day.count, isSelected)} transition-colors cursor-pointer hover:ring-2 hover:ring-slate-300 dark:hover:ring-slate-600 outline-none`}
                                            data-tooltip-id="heatmap-tooltip"
                                            data-tooltip-content={`${day.date}: ${day.count} tasks`}
                                            onClick={() => onDateSelect?.(day.obj)}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-3 px-1">
                        <span>6 months ago</span>
                        <span>Today</span>
                    </div>
                </motion.div>
            ) : (
                <motion.div
                    key="calendar"
                    initial={{ opacity: 0, position: 'absolute', width: '100%' }}
                    animate={{ opacity: 1, position: 'relative', width: '100%' }}
                    exit={{ opacity: 0, position: 'absolute', width: '100%' }}
                    transition={{ duration: 0.3 }}
                    className="w-full px-1"
                >
                    <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                        {['日', '一', '二', '三', '四', '五', '六'].map(d => (
                            <div key={d} className="text-xs text-slate-400 py-1">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7 gap-2 pb-2">
                        {calendarDays.map((item, idx) => {
                            if (!item) return <div key={`blank-${idx}`} className="aspect-square" />;
                            
                            const isToday = new Date().toDateString() === item.date.toDateString();
                            const isSelected = selectedDate && item.date.toDateString() === selectedDate.toDateString();
                            const count = item.count;
                            const hasActivity = count > 0;
                            
                            let bgClass = 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700';
                                
                            // Override for selected
                            if (isSelected) {
                                bgClass = 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
                            } 

                            return (
                                <button
                                    key={item.day}
                                    onClick={() => onDateSelect?.(item.date)}
                                    className={`aspect-square rounded-xl flex flex-col items-center justify-start pt-2 relative transition-all border ${
                                        isSelected
                                        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-500 shadow-sm'
                                        : `bg-white dark:bg-slate-800 border-transparent hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-sm`
                                    } ${isToday && !isSelected ? 'ring-2 ring-blue-400 ring-offset-1 dark:ring-offset-slate-900' : ''}`}
                                >
                                    <span className={`text-xs font-medium ${
                                        isSelected ? 'text-orange-600 dark:text-orange-400' : 
                                        'text-slate-700 dark:text-slate-300'
                                    }`}>
                                        {item.day}
                                    </span>
                                    
                                    {hasActivity && (
                                        <div className={`mt-1 h-5 min-w-[20px] px-1.5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                            isSelected ? 'bg-orange-500 text-white' : getBadgeColor(count)
                                        }`}>
                                            {count}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>
            )}
            </AnimatePresence>
        </motion.div>
        
        <Tooltip id="heatmap-tooltip" className="z-50 !bg-slate-800 !text-xs !px-2 !py-1 !rounded" />
    </div>
  );
};

export default TodoHeatmap;
